// src/encounter/referral-card-section.tsx
// [PROD-1395 / PROD-3336] Pre-encounter "Referral" card for the allied-health clinician: who sent the patient, when, why,
// the note and where the referral stands. Modelled on previous-notes-section.tsx (EncounterSectionHeader + the card
// class, lifted by useDesignV2). Behind the `referrals` flag. It draws NOTHING unless the flag is on, the clinician is
// allied health and the back end returned an open referral ("if the back end showed them, they will appear").
import { useState } from 'react';
import { cn, EncounterSectionHeader, StatusChip, TEXT_ROLE_CLASSES, TEXT_TONE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { useDesignV2 } from './lib/design-v2';
import { newestReferral, toReferralCardView } from './lib/to-referral-card';
import { usePatientReferrals } from './services/use-patient-referrals';
import { useMySpecialityIds } from './services/use-my-speciality-ids';
import type { ShellRole } from './lib/encounter-blocks';
import { ReferralHandoff } from '@scripsteam/signal';
import { receivedReferralHandoff } from './lib/to-referral-handoff';
import { SignalScope } from './nurse-med-rec/nurse-med-rec-block';

export interface ReferralCardSectionProps {
  patientId?: string | null;
  encounterRole?: ShellRole;
}

const CARD = 'bg-surface-card border border-border-default rounded-lg p-5';
/** Design sheet v1.1 §1 Surfaces (switch ON only): the card lifts, with the card border and `--shadow-2`. */
const CARD_V2 = 'bg-surface-card border border-border-card shadow-2 rounded-lg p-5';
const STACK_2 = 'flex flex-col gap-2';
const HEAD_ROW = 'flex items-start justify-between gap-4';

export function ReferralCardSection({ patientId, encounterRole }: ReferralCardSectionProps) {
  // Read once per mount, like the other encounter switches.
  const [referralsOn] = useState(() => isEncounterFlagOn('referrals'));
  // The read lives in the inner component, so flag off or a physician mounts no query at all.
  // `referralHandoff` (dev default): the incoming referral as Antonin's hand-off card (catalogue 07), same read. OFF: today's card.
  const [handoffOn] = useState(() => isEncounterFlagOn('referralHandoff'));
  if (!referralsOn || encounterRole !== 'allied-health') return null;
  return <IncomingReferral patientId={patientId} handoff={handoffOn} />;
}

function IncomingReferral({ patientId, handoff }: { patientId?: string | null; handoff?: boolean }) {
  const designV2 = useDesignV2();
  // PROD-3336: the referrals sent to this clinician's own specialities, in ONE request once those are known. The PROD-1395
  // dietitian rows (performerRole, OR'd by the server) are added only for a clinician of the dietetics discipline, so a
  // coach does not see a dietitian's referrals. If the clinician's specialities cannot be read, the dietitian rows are read
  // as before PROD-3336, so a dietitian never loses the card. With no filter at all nothing is read.
  const mine = useMySpecialityIds(true);
  const role = mine.failed || mine.dietetics ? 'dietitian' : null;
  const { data } = usePatientReferrals(patientId, mine.settled, role, mine.ids);
  const row = newestReferral(data);
  if (!row) return null;
  if (handoff) {
    // Every line on the card is Antonin's (ReferralHandoff, side "received"), inside the scoped tokens: nothing drawn here.
    return (
      <section aria-label="Referral" data-testid="referral-handoff-received">
        <SignalScope slot="referral-handoff-received">
          <ReferralHandoff view={receivedReferralHandoff(row)} side="received" />
        </SignalScope>
      </section>
    );
  }
  const view = toReferralCardView(row);

  return (
    <section className={designV2 ? CARD_V2 : CARD} aria-label="Referral">
      <EncounterSectionHeader title="Referral" variant="section" />
      <div className="h-3" />
      <div className={STACK_2}>
        <div className={HEAD_ROW}>
          <span className={cn('min-w-0 flex-1', TEXT_ROLE_CLASSES['body-emphasis'])}>
            {view.referrer ? `From ${view.referrer}` : 'Referral'}
          </span>
          {view.status && (
            <span className="shrink-0">
              <StatusChip semantic={view.status.semantic} tone="soft">
                {view.status.label}
              </StatusChip>
            </span>
          )}
        </div>
        {view.date && <span className={cn(TEXT_ROLE_CLASSES.meta, TEXT_TONE_CLASSES.secondary)}>{view.date}</span>}
        {view.reason && (
          <div className="flex flex-col gap-1">
            <span className={TEXT_ROLE_CLASSES['field-label']}>Reason</span>
            <span className={TEXT_ROLE_CLASSES.body}>{view.reason}</span>
          </div>
        )}
        {view.note && (
          <div className="flex flex-col gap-1">
            <span className={TEXT_ROLE_CLASSES['field-label']}>Note</span>
            <p className={cn('m-0 whitespace-pre-wrap', TEXT_ROLE_CLASSES.body)}>{view.note}</p>
          </div>
        )}
      </div>
    </section>
  );
}
